// src/features/booking/ReviewDialog.tsx
// Leave a review for a completed booking.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { FormDialog, TitleInput, AutoGrowTextarea } from '@/components/form';
import { errMessage } from './lib';
import { CreateReview } from '@core/api/endpoints/booking/create_review';
import type { Booking } from '@core/types/booking';

interface Props {
  open: boolean;
  onClose: () => void;
  booking: Booking | null;
  onSubmitted: () => void;
}

function StarRow({ value, onChange, label }: { value: number; onChange: (v: number) => void; label: string }) {
  return (
    <div>
      <span className="mb-1 block text-xs text-muted-foreground">{label}</span>
      <div className="flex gap-1" role="radiogroup" aria-label={label}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            onClick={() => onChange(n)}
            className={`text-lg ${n <= value ? 'text-warning' : 'text-muted-foreground'}`}
          >
            ★
          </button>
        ))}
      </div>
    </div>
  );
}

export function ReviewDialog({ open, onClose, booking, onSubmitted }: Props) {
  const { t } = useI18n();
  const [overall, setOverall] = useState(5);
  const [punctuality, setPunctuality] = useState(0);
  const [quality, setQuality] = useState(0);
  const [communication, setCommunication] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOverall(5);
    setPunctuality(0);
    setQuality(0);
    setCommunication(0);
    setTitle('');
    setComment('');
    setError(null);
  }, [open]);

  const submit = async () => {
    if (!booking?.entity) return;
    setSaving(true);
    setError(null);
    try {
      await CreateReview(booking.entity.type_name || 'booking', booking.entity.id, {
        target: booking.entity,
        direction: 'REVIEW_DIRECTION_CUSTOMER_TO_PROVIDER',
        overall_rating: overall,
        punctuality_rating: punctuality || undefined,
        quality_rating: quality || undefined,
        communication_rating: communication || undefined,
        title: title || undefined,
        comment: comment || undefined,
        image_urls: [],
      });
      setSaving(false);
      onSubmitted();
      onClose();
    } catch (e) {
      setSaving(false);
      setError(errMessage(e, t('booking.review.error')));
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('booking.review.submit')}
      isSubmitting={saving}
      dirty={!!(title.trim() || comment.trim() || punctuality || quality || communication)}
      aria-label={t('booking.review.title')}
    >
      <div className="space-y-3 px-3.5 pb-2 pt-3">
        {error && <Alert variant="error">{error}</Alert>}
        <StarRow value={overall} onChange={setOverall} label={t('booking.review.overall')} />
        <div className="grid grid-cols-3 gap-3">
          <StarRow value={punctuality} onChange={setPunctuality} label={t('booking.review.punctuality')} />
          <StarRow value={quality} onChange={setQuality} label={t('booking.review.quality')} />
          <StarRow value={communication} onChange={setCommunication} label={t('booking.review.communication')} />
        </div>
        <TitleInput
          value={title}
          onChange={setTitle}
          placeholder={`${t('booking.review.reviewTitle')}…`}
          className="text-base font-medium"
          aria-label={t('booking.review.reviewTitle')}
        />
        <AutoGrowTextarea value={comment} onChange={setComment} placeholder={`${t('booking.review.comment')}…`} />
      </div>
    </FormDialog>
  );
}
